/**
 * „Anpassen": die vier Ecken einer Seite auf dem Originalbild verschieben.
 *
 * Beim Ziehen zeigt eine Lupe die Umgebung der Ecke vergrößert — der Finger
 * verdeckt sonst genau die Stelle, auf die es ankommt. Die Griffe sind 44 px
 * groß (Apple-Mindestmaß), die Lupe sitzt oberhalb des Fingers.
 */
import { useEffect, useRef, useState } from "react";
import type { Ecken, Punkt } from "../../lib/scan/bild";
import { ScanIcon } from "./ScanIcon";

type Props = {
  bild: Blob;
  w: number;
  h: number;
  ecken: Ecken;
  onUebernehmen: (e: Ecken) => void;
  onAbbrechen: () => void;
};

type Ecke = keyof Ecken;
const RAND = 26;
const LUPE = 120;
const ZOOM = 2.2;

export default function EckenEditor({ bild, w, h, ecken, onUebernehmen, onAbbrechen }: Props) {
  const [url] = useState(() => URL.createObjectURL(bild));
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  const flaecheRef = useRef<HTMLDivElement>(null);
  const [lay, setLay] = useState({ s: 1, x: 0, y: 0, bw: 0, bh: 0 });
  const [e, setE] = useState<Ecken>(ecken);
  const [zieht, setZieht] = useState<Ecke | null>(null);

  useEffect(() => {
    const rechne = () => {
      const el = flaecheRef.current;
      if (!el) return;
      const cw = el.clientWidth - 2 * RAND;
      const ch = el.clientHeight - 2 * RAND;
      const s = Math.min(cw / w, ch / h);
      setLay({ s, x: RAND + (cw - w * s) / 2, y: RAND + (ch - h * s) / 2, bw: w * s, bh: h * s });
    };
    rechne();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(rechne) : null;
    if (ro && flaecheRef.current) ro.observe(flaecheRef.current);
    return () => ro?.disconnect();
  }, [w, h]);

  const zuBild = (cx: number, cy: number): Punkt => {
    const r = flaecheRef.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(w, (cx - r.left - lay.x) / lay.s)),
      y: Math.max(0, Math.min(h, (cy - r.top - lay.y) / lay.s)),
    };
  };
  const zuSchirm = (p: Punkt) => ({ x: lay.x + p.x * lay.s, y: lay.y + p.y * lay.s });

  const runter = (k: Ecke) => (ev: React.PointerEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    (ev.currentTarget as HTMLElement).setPointerCapture?.(ev.pointerId);
    setZieht(k);
  };
  const bewegen = (ev: React.PointerEvent) => {
    if (!zieht) return;
    const p = zuBild(ev.clientX, ev.clientY);
    setE((alt) => ({ ...alt, [zieht]: p }));
  };
  const hoch = () => setZieht(null);

  const p = { tl: zuSchirm(e.tl), tr: zuSchirm(e.tr), br: zuSchirm(e.br), bl: zuSchirm(e.bl) };
  const lupe = zieht ? p[zieht] : null;
  const lupePunkt = zieht ? e[zieht] : null;

  return (
    <div className="sc-editor">
      <div className="sc-editor-kopf">
        <button className="sc-textknopf" onClick={onAbbrechen}>Abbrechen</button>
        <div className="sc-editor-titel">Ecken anpassen</div>
        <button className="sc-textknopf is-primaer" onClick={() => onUebernehmen(e)}>Fertig</button>
      </div>
      <div
        ref={flaecheRef}
        className="sc-editor-flaeche"
        onPointerMove={bewegen}
        onPointerUp={hoch}
        onPointerCancel={hoch}
      >
        <img
          src={url}
          alt=""
          draggable={false}
          style={{ left: lay.x, top: lay.y, width: lay.bw, height: lay.bh }}
        />
        <svg className="sc-editor-svg">
          <path
            className="sc-editor-maske"
            fillRule="evenodd"
            d={`M0 0H10000V10000H0Z M${p.tl.x} ${p.tl.y}L${p.tr.x} ${p.tr.y}L${p.br.x} ${p.br.y}L${p.bl.x} ${p.bl.y}Z`}
          />
          <polygon
            className="sc-editor-linie"
            points={`${p.tl.x},${p.tl.y} ${p.tr.x},${p.tr.y} ${p.br.x},${p.br.y} ${p.bl.x},${p.bl.y}`}
          />
        </svg>
        {(["tl", "tr", "br", "bl"] as Ecke[]).map((k) => (
          <div
            key={k}
            className={"sc-griff" + (zieht === k ? " is-aktiv" : "")}
            style={{ left: p[k].x, top: p[k].y }}
            onPointerDown={runter(k)}
          />
        ))}
        {lupe && lupePunkt && (
          <div
            className="sc-lupe"
            style={{
              left: Math.max(LUPE / 2 + 4, Math.min(lay.x * 2 + lay.bw - LUPE / 2, lupe.x)),
              // oberhalb des Fingers; ganz oben im Bild darunter
              top: lupe.y > LUPE * 1.1 ? lupe.y - LUPE * 1.05 : lupe.y + LUPE * 0.45,
              width: LUPE,
              height: LUPE,
              backgroundImage: `url(${url})`,
              backgroundSize: `${lay.bw * ZOOM}px ${lay.bh * ZOOM}px`,
              backgroundPosition: `${LUPE / 2 - lupePunkt.x * lay.s * ZOOM}px ${LUPE / 2 - lupePunkt.y * lay.s * ZOOM}px`,
            }}
          >
            <span className="sc-lupe-kreuz" />
          </div>
        )}
      </div>
      <div className="sc-editor-fuss">
        <button
          className="sc-knopf sc-knopf-leise"
          onClick={() =>
            setE({ tl: { x: 0, y: 0 }, tr: { x: w, y: 0 }, br: { x: w, y: h }, bl: { x: 0, y: h } })
          }
        >
          <ScanIcon name="anpassen" /> Ganzes Bild
        </button>
        <button className="sc-knopf sc-knopf-leise" onClick={() => setE(ecken)}>
          Zurücksetzen
        </button>
      </div>
    </div>
  );
}
